import type { FREQUENCY_DISTRIBUTION_CALCULATOR_STRINGS } from "@/components/tools/frequency-distribution-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  formatStat,
  machineNumber,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type CategoryOrder,
  categoryFrequencies,
  type Frequencies,
  type GroupedError,
  type GroupedTable,
  groupedFrequencies,
  isGroupedError,
  MAX_CLASSES,
  MAX_UNGROUPED_ROWS,
  ungroupedFrequencies,
} from "@/lib/tool-kit/stats-descriptive/frequency";

/*
 * The frequency-table widget's computations: turn the settings into a table model —
 * display cells for the page, ASCII cells for the clipboard — or a reason the table
 * cannot be built. Every word comes from the widget's strings.
 */

type Strings = typeof FREQUENCY_DISTRIBUTION_CALCULATOR_STRINGS;
export type DataType = "numbers" | "categories";
export type TableType = "grouped" | "ungrouped";
export type ClassesBy = "count" | "width";

export interface TableModel {
  head: string[];
  /** Data rows as the reader reads them. */
  display: string[][];
  /** Data rows for the clipboard: ASCII numbers. */
  machine: string[][];
  /** The total row, in both forms. */
  totalDisplay: string[];
  totalMachine: string[];
}

const PERCENT_DIGITS = 2;

function frequencyCells(
  row: Frequencies,
  fmt: NumberFormatter
): { display: string[]; machine: string[] } {
  return {
    display: [
      fmt(row.frequency),
      fmt(row.relative * 100, { maximumFractionDigits: PERCENT_DIGITS }),
      fmt(row.cumulative),
      fmt(row.cumulativeRelative * 100, {
        maximumFractionDigits: PERCENT_DIGITS,
      }),
    ],
    machine: [
      String(row.frequency),
      machineNumber(row.relative * 100, 4),
      String(row.cumulative),
      machineNumber(row.cumulativeRelative * 100, 4),
    ],
  };
}

function buildModel<R extends Frequencies>(
  rows: readonly R[],
  n: number,
  leading: (row: R) => { display: string[]; machine: string[] },
  head: string[],
  strings: Strings,
  fmt: NumberFormatter
): TableModel {
  const display: string[][] = [];
  const machine: string[][] = [];
  for (const row of rows) {
    const lead = leading(row);
    const cells = frequencyCells(row, fmt);
    display.push([...lead.display, ...cells.display]);
    machine.push([...lead.machine, ...cells.machine]);
  }
  const blanks = head.length - 5;
  const pad = new Array<string>(blanks).fill("");
  return {
    head,
    display,
    machine,
    totalDisplay: [strings.total, ...pad, fmt(n), fmt(100), "", ""],
    totalMachine: [strings.total, ...pad, String(n), "100", "", ""],
  };
}

function frequencyHead(strings: Strings): string[] {
  return [
    strings.colFrequency,
    strings.colRelative,
    strings.colCumulative,
    strings.colCumulativeRelative,
  ];
}

function groupedModel(
  table: GroupedTable,
  strings: Strings,
  fmt: NumberFormatter
): TableModel {
  const range = (from: string, to: string) =>
    t(strings.rangeTemplate, { from, to });
  return buildModel(
    table.rows,
    table.n,
    (row) => ({
      display: [
        range(formatStat(fmt, row.lower), formatStat(fmt, row.upper)),
        range(
          formatStat(fmt, row.lowerBoundary),
          formatStat(fmt, row.upperBoundary)
        ),
        formatStat(fmt, row.midpoint),
      ],
      machine: [
        range(machineNumber(row.lower), machineNumber(row.upper)),
        range(
          machineNumber(row.lowerBoundary),
          machineNumber(row.upperBoundary)
        ),
        machineNumber(row.midpoint),
      ],
    }),
    [
      strings.colClass,
      strings.colBoundaries,
      strings.colMidpoint,
      ...frequencyHead(strings),
    ],
    strings,
    fmt
  );
}

function groupedErrorMessage(
  error: GroupedError,
  strings: Strings,
  fmt: NumberFormatter,
  locale: string
): string {
  if (error.error === "start-above-minimum") {
    return t(strings.errorStart, { min: formatStat(fmt, error.min) });
  }
  if (error.error === "too-many-classes") {
    return tPlural(strings.errorTooMany, locale, error.classCount, {
      count: fmt(error.classCount),
      max: fmt(MAX_CLASSES),
    });
  }
  return error.error === "invalid-width"
    ? strings.errorWidth
    : strings.errorCount;
}

/**
 * The "leave blank for Sturges' rule" hint. It names two counted nouns — the classes
 * the rule gives and the values it was given — so each is its own plural message and the
 * sentence around them is plain text a translator can reorder.
 */
export function sturgesHint(
  strings: Strings,
  locale: string,
  fmt: NumberFormatter,
  classCount: number,
  valueCount: number
): string {
  return t(strings.classCountHint, {
    classes: tPlural(strings.classCountHintClasses, locale, classCount, {
      count: fmt(classCount),
    }),
    values: tPlural(strings.classCountHintValues, locale, valueCount, {
      count: fmt(valueCount),
    }),
  });
}

export interface Settings {
  dataType: DataType;
  values: number[];
  items: string[];
  tableType: TableType;
  classesBy: ClassesBy;
  countText: string;
  widthText: string;
  startText: string;
  order: CategoryOrder;
  ignoreCase: boolean;
}

interface Computed {
  model: TableModel | null;
  grouped: GroupedTable | null;
  /** A setting the table cannot be built from. */
  problem: string | null;
}

const EMPTY: Computed = { model: null, grouped: null, problem: null };

function computeGrouped(
  settings: Settings,
  strings: Strings,
  fmt: NumberFormatter,
  locale: string
): Computed {
  const { classesBy, countText, widthText, startText, values } = settings;
  if (classesBy === "width" && widthText.trim() === "") {
    return EMPTY;
  }
  // Blank means Sturges' rule; anything else must be a whole number.
  const classCount =
    countText.trim() === ""
      ? undefined
      : (parseIntegerInput(countText) ?? Number.NaN);
  const result = groupedFrequencies(values, {
    classCount: classesBy === "count" ? classCount : undefined,
    width:
      classesBy === "width" ? (parseNumberInput(widthText) ?? 0) : undefined,
    start: parseNumberInput(startText) ?? undefined,
  });
  if (result === null) {
    return EMPTY;
  }
  if (isGroupedError(result)) {
    return {
      model: null,
      grouped: null,
      problem: groupedErrorMessage(result, strings, fmt, locale),
    };
  }
  return {
    model: groupedModel(result, strings, fmt),
    grouped: result,
    problem: null,
  };
}

export function computeTable(
  settings: Settings,
  strings: Strings,
  fmt: NumberFormatter,
  locale: string
): Computed {
  const { dataType, values, items } = settings;
  const n = dataType === "numbers" ? values.length : items.length;
  if (n < 2) {
    return EMPTY;
  }
  if (dataType === "categories") {
    const rows = categoryFrequencies(items, {
      ignoreCase: settings.ignoreCase,
      order: settings.order,
    });
    const model = buildModel(
      rows,
      n,
      (row) => ({ display: [row.category], machine: [row.category] }),
      [strings.colCategory, ...frequencyHead(strings)],
      strings,
      fmt
    );
    return { model, grouped: null, problem: null };
  }
  if (settings.tableType === "grouped") {
    return computeGrouped(settings, strings, fmt, locale);
  }
  const rows = ungroupedFrequencies(values);
  if (rows.length > MAX_UNGROUPED_ROWS) {
    return {
      model: null,
      grouped: null,
      problem: tPlural(strings.tooManyRows, locale, rows.length, {
        count: fmt(rows.length),
      }),
    };
  }
  const model = buildModel(
    rows,
    n,
    (row) => ({
      display: [formatStat(fmt, row.value)],
      machine: [machineNumber(row.value)],
    }),
    [strings.colValue, ...frequencyHead(strings)],
    strings,
    fmt
  );
  return { model, grouped: null, problem: null };
}

export function promptFor(
  settings: Settings,
  n: number,
  strings: Strings
): string {
  if (
    n >= 2 &&
    settings.dataType === "numbers" &&
    settings.tableType === "grouped" &&
    settings.classesBy === "width"
  ) {
    return strings.enterWidth;
  }
  return strings.enterPrompt;
}
